// ======================================
// Home Page Module
// ======================================

import { icons, renderServiceCard, renderTestimonialCard, renderPackageCard, renderProjectCard } from '../components.js';
import { howItWorksSteps, whyChooseUs } from '../data.js';
import { servicesAPI, portfolioAPI, testimonialsAPI, packagesAPI } from '../lib/api.js';

export function renderHomePage() {
  return `
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg">
        <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&h=1080&fit=crop" alt="Modern interior design" class="hero-bg-image">
        <div class="hero-bg-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="hero-grid">
          <div class="hero-text">
            <div class="hero-badge">
              ${icons.sparkles}
              <span>Premium Interior Design</span>
            </div>
            <h1 class="hero-title">
              Transform Your Home Into A <span class="hero-title-accent">Dream Space</span>
            </h1>
            <p class="hero-subtitle">
              Expert interior design services with transparent pricing, 45-day delivery guarantee, 
              and a 10-year warranty. From modular kitchens to full home makeovers.
            </p>
            <div class="hero-ctas">
              <a href="/estimator" class="btn btn-primary btn-lg">
                Get Free Estimate
                ${icons.arrowRight}
              </a>
              <a href="/projects" class="btn btn-secondary btn-lg">
                View Our Work
              </a>
            </div>
            <div class="hero-stats">
              <div class="hero-stat">
                <div class="hero-stat-value">500+</div>
                <div class="hero-stat-label">Happy Homes</div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-value">10</div>
                <div class="hero-stat-label">Year Warranty</div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-value">45</div>
                <div class="hero-stat-label">Day Delivery</div>
              </div>
            </div>
          </div>
          <div class="hero-image">
            <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=500&fit=crop" alt="Beautiful living room" class="hero-image-main">
            <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&h=200&fit=crop" alt="Modern kitchen" class="hero-image-accent">
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us Section -->
    <section class="section why-us">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Why Choose NammaVeetuInteriors?</h2>
          <p class="section-subtitle">
            We're not just designers, we're your partners in creating spaces you'll love coming home to.
          </p>
        </div>
        <div class="why-us-grid">
          ${whyChooseUs.map(item => `
            <div class="why-us-card">
              <div class="why-us-icon">
                ${icons[item.icon]}
              </div>
              <h3 class="why-us-title">${item.title}</h3>
              <p class="why-us-desc">${item.description}</p>
            </div>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- Services Preview Section -->
    <section class="section" style="background-color: var(--color-base);">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Our Interior Services</h2>
          <p class="section-subtitle">
            From modular kitchens to complete home transformations, we handle it all.
          </p>
        </div>
        <div class="services-grid" id="home-services-grid">
          <div class="loading-spinner">Loading Services...</div>
        </div>
        <div style="text-align: center; margin-top: var(--space-10);">
          <a href="/services" class="btn btn-secondary">View All Services ${icons.arrowRight}</a>
        </div>
      </div>
    </section>

    <!-- Packages Preview Section -->
    <section class="section" style="background-color: var(--color-surface);">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Interior Packages</h2>
          <p class="section-subtitle">
            All-inclusive packages designed for different budgets and needs.
          </p>
        </div>
        <div class="packages-grid" id="home-packages-grid">
           <div class="loading-spinner">Loading Packages...</div>
        </div>
        <div style="text-align: center; margin-top: var(--space-10);">
          <a href="/packages" class="btn btn-secondary">View All Packages ${icons.arrowRight}</a>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="section how-it-works">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">How It Works</h2>
          <p class="section-subtitle">
            Our streamlined process makes getting your dream interiors simple and stress-free.
          </p>
        </div>
        <div class="steps-container">
          ${howItWorksSteps.map((step, index) => `
            <div class="step-item">
              <div class="step-number">${step.number}</div>
              <div class="step-content">
                <h3 class="step-title">${step.title}</h3>
                <p class="step-desc">${step.description}</p>
              </div>
            </div>
            ${index < howItWorksSteps.length - 1 ? '<div class="step-line"></div>' : ''}
          `).join('')}
        </div>
        <div style="text-align: center; margin-top: var(--space-12);">
          <a href="/contact" class="btn btn-primary btn-lg">Start Your Project</a>
        </div>
      </div>
    </section>

    <!-- Portfolio Highlights Section -->
    <section class="section" style="background-color: var(--color-surface);">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Recent Projects</h2>
          <p class="section-subtitle">
            See what we've created for families just like yours.
          </p>
        </div>
        <div class="projects-grid" id="home-projects-grid">
           <div class="loading-spinner">Loading Projects...</div>
        </div>
        <div style="text-align: center; margin-top: var(--space-10);">
          <a href="/projects" class="btn btn-secondary">View All Projects ${icons.arrowRight}</a>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="section" style="background-color: var(--color-base);">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">What Our Clients Say</h2>
          <p class="section-subtitle">
            Don't just take our word for it. Hear from families who trusted us.
          </p>
        </div>
        <div class="testimonials-grid" id="home-testimonials-grid">
           <div class="loading-spinner">Loading Reviews...</div>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Ready to Transform Your Home?</h2>
          <p class="cta-subtitle">
            Get a free consultation and detailed quote. No obligations, no pressure.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Book Free Consultation
          </button>
        </div>
      </div>
    </section>
  `;
}

export function initHomePage() {
  loadHomeData();
}

async function loadHomeData() {
  loadServices();
  loadProjects();
  loadPackages();
  loadTestimonials();
}

async function loadServices() {
  const grid = document.getElementById('home-services-grid');
  if (!grid) return;
  try {
    const response = await servicesAPI.list();
    const items = Array.isArray(response) ? response : (response.data || []);
    if (items.length) {
      grid.innerHTML = items.slice(0, 6).map(s => renderServiceCard(s)).join('');
    } else {
      grid.innerHTML = '<p class="text-center text-muted col-span-full">No services found.</p>';
    }
  } catch (e) {
    console.error('Home Services Error:', e);
    grid.innerHTML = '<p class="text-center text-error col-span-full">Failed to load services.</p>';
  }
}

async function loadProjects() {
  const grid = document.getElementById('home-projects-grid');
  if (!grid) return;
  try {
    // Use portfolioAPI for public showcase
    const response = await portfolioAPI.list({ limit: 3 });
    const items = Array.isArray(response) ? response : (response.data || []);

    if (items.length) {
      grid.innerHTML = items.slice(0, 3).map(p => {
        // Map portfolio fields to project card props
        return renderProjectCard({
          ...p,
          image: p.featured_image || p.image,
          location: p.city || p.location,
          bhk: p.category, // or p.bhk if available
          total_amount: p.budget_range || p.total_amount,
          duration: p.duration_days ? `${p.duration_days} Days` : p.duration
        });
      }).join('');
    } else {
      grid.innerHTML = '<p class="text-center text-muted col-span-full">No projects found.</p>';
    }
  } catch (e) {
    console.error('Home Projects Error:', e);
    grid.innerHTML = '<p class="text-center text-error col-span-full">Failed to load projects.</p>';
  }
}

async function loadPackages() {
  const grid = document.getElementById('home-packages-grid');
  if (!grid) return;
  try {
    const response = await packagesAPI.list();
    const items = Array.isArray(response) ? response : (response.data || []);
    if (items.length) {
      grid.innerHTML = items.map((pkg, i) => renderPackageCard(pkg, pkg.is_featured)).join('');
    } else {
      grid.innerHTML = '<p class="text-center text-muted col-span-full">No packages found.</p>';
    }
  } catch (e) {
    console.error('Home Packages Error:', e);
    grid.innerHTML = '<p class="text-center text-error col-span-full">Failed to load packages.</p>';
  }
}

async function loadTestimonials() {
  const grid = document.getElementById('home-testimonials-grid');
  if (!grid) return;
  try {
    const response = await testimonialsAPI.list({ limit: 3, featured: true }); // Assuming filter param
    const items = Array.isArray(response) ? response : (response.items || []);
    if (items.length) {
      grid.innerHTML = items.slice(0, 3).map(t => renderTestimonialCard(t)).join('');
    } else {
      grid.innerHTML = '<p class="text-center text-muted col-span-full">No testimonials yet.</p>';
    }
  } catch (e) {
    console.error('Home Testimonials Error:', e);
    grid.innerHTML = '<p class="text-center text-error col-span-full">Failed to load testimonials.</p>';
  }
}
